import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Typography,
    Grid,
    Box,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Breadcrumbs,
    Chip,
    IconButton,
    Tooltip,
    Divider,
    Card,
    CardContent,
    CardHeader,
    Stack,
    List,
    ListItem,
    ListItemText,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormLabel,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Badge as BadgeIcon,
    LockReset as LockResetIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';

import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import { userAPI, memberProfileAPI } from '../../services/api';
import { User, UserRole, UserStatus } from '../../types';
import { MemberProfile } from '../../types/memberProfile';
import { useAuth } from '../../context/AuthContext';
import ErrorBoundary from '../common/ErrorBoundary';

const UserDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const [user, setUser] = useState<User | null>(null);
    const [memberProfiles, setMemberProfiles] = useState<MemberProfile[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
    const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState<boolean>(false);
    const [processingDelete, setProcessingDelete] = useState<boolean>(false);
    const [processingEdit, setProcessingEdit] = useState<boolean>(false);
    const [processingPasswordReset, setProcessingPasswordReset] = useState<boolean>(false);

    // Edit form state
    const [editForm, setEditForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: '',
        status: ''
    });

    // Password reset form state
    const [passwordResetForm, setPasswordResetForm] = useState({
        resetType: 'set_password', // 'set_password' or 'send_reset_link'
        newPassword: '',
        confirmPassword: ''
    });

    // Password visibility state
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Fetch user details
    const fetchUserDetails = async () => {
        if (!id) return;

        setLoading(true);
        try {
            const userResponse = await userAPI.getUserById(id);
            const userData = userResponse.data;
            setUser(userData);

            // Set edit form with current user data
            setEditForm({
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                email: userData.email || '',
                phone: userData.phone || '',
                role: userData.role || '',
                status: userData.status || ''
            });

            // Fetch user member profiles
            try {
                const membershipsResponse = await memberProfileAPI.getUserMemberProfiles(id);
                setMemberProfiles(membershipsResponse.data);
            } catch (membershipError) {
                console.error('Error fetching member profiles:', membershipError);
            }

            setError(null);
        } catch (err: any) {
            console.error('Error fetching user details:', err);
            setError('Failed to load user details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUserDetails();
    }, [id]);

    // Handle back navigation
    const handleBack = () => {
        navigate('/user-management');
    };

    // Handle edit dialog
    const handleEditOpen = () => {
        setEditDialogOpen(true);
    };

    const handleEditClose = () => {
        // Reset form to original user data when closing without saving
        if (user) {
            setEditForm({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                phone: user.phone || '',
                role: user.role || '',
                status: user.status || ''
            });
        }
        setEditDialogOpen(false);
    };

    const handleEditSubmit = async () => {
        if (!id || !user) return;

        setProcessingEdit(true);
        try {
            // Update the user first
            await userAPI.updateUser(id, editForm);
            
            // If email was changed, update all associated member profiles
            const emailChanged = editForm.email !== user.email;
            let memberProfileErrors: string[] = [];
            
            if (emailChanged && memberProfiles.length > 0) {
                console.log('Email changed, updating member profiles...');
                
                // Process member profile updates and collect any errors
                const updateResults = await Promise.allSettled(
                    memberProfiles.map(async (profile) => {
                        try {
                            await memberProfileAPI.updateMemberProfile(profile.id, {
                                email: editForm.email
                            });
                            console.log(`Updated member profile ${profile.id} with new email`);
                            return { success: true, profileId: profile.id };
                        } catch (profileError) {
                            console.error(`Failed to update member profile ${profile.id}:`, profileError);
                            memberProfileErrors.push(`Profile ${profile.firstName} ${profile.lastName}`);
                            return { success: false, profileId: profile.id, error: profileError };
                        }
                    })
                );
                
                const successfulUpdates = updateResults.filter(result => result.status === 'fulfilled' && result.value.success).length;
                const failedUpdates = updateResults.filter(result => result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success)).length;
                
                console.log(`Member profile updates: ${successfulUpdates} successful, ${failedUpdates} failed`);
            }
            
            // Set appropriate success message
            let successMessage = 'User updated successfully!';
            
            if (emailChanged && memberProfiles.length > 0) {
                if (memberProfileErrors.length === 0) {
                    successMessage = `User updated successfully! Email address also updated in ${memberProfiles.length} member profile(s).`;
                } else if (memberProfileErrors.length < memberProfiles.length) {
                    const successfulCount = memberProfiles.length - memberProfileErrors.length;
                    successMessage = `User updated successfully! Email updated in ${successfulCount} of ${memberProfiles.length} member profiles. Some profiles failed to update.`;
                    // Also set a warning for the failed updates
                    setTimeout(() => {
                        setError(`Warning: Failed to update email in these member profiles: ${memberProfileErrors.join(', ')}`);
                        setTimeout(() => setError(null), 8000);
                    }, 2000);
                } else {
                    successMessage = 'User updated successfully, but failed to update email in member profiles.';
                    setTimeout(() => {
                        setError('Failed to update email in all member profiles. You may need to update them manually.');
                        setTimeout(() => setError(null), 8000);
                    }, 2000);
                }
            }
            
            setSuccess(successMessage);
            setEditDialogOpen(false);
            fetchUserDetails(); // Refresh data

            // Clear success message after 5 seconds
            setTimeout(() => {
                setSuccess(null);
            }, 5000);
        } catch (err: any) {
            console.error('Error updating user:', err);
            setError(err.response?.data?.message || 'Failed to update user. Please try again.');
        } finally {
            setProcessingEdit(false);
        }
    };

    const handleEditChange = (field: string, value: string) => {
        setEditForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle delete dialog
    const handleDeleteOpen = () => {
        setDeleteDialogOpen(true);
    };

    const handleDeleteClose = () => {
        setDeleteDialogOpen(false);
    };

    const handleDeleteConfirm = async () => {
        if (!id || !user) return;

        setProcessingDelete(true);
        try {
            // Instead of deleting, set user status to inactive
            await userAPI.updateUser(id, { status: 'inactive' });
            setSuccess('User account has been set to inactive!');
            setDeleteDialogOpen(false);
            fetchUserDetails(); // Refresh data

            // Clear success message after 5 seconds
            setTimeout(() => {
                setSuccess(null);
            }, 5000);
        } catch (err: any) {
            console.error('Error deactivating user:', err);
            setError(err.response?.data?.message || 'Failed to deactivate user. Please try again.');
        } finally {
            setProcessingDelete(false);
        }
    };

    // Handle password reset dialog
    const handlePasswordResetOpen = () => {
        setPasswordResetForm({
            resetType: 'set_password',
            newPassword: '',
            confirmPassword: ''
        });
        setPasswordResetDialogOpen(true);
    };

    const handlePasswordResetClose = () => {
        setPasswordResetDialogOpen(false);
        setPasswordResetForm({
            resetType: 'set_password',
            newPassword: '',
            confirmPassword: ''
        });
        setShowPassword(false);
        setShowConfirmPassword(false);
    };

    const handlePasswordResetSubmit = async () => {
        if (!id || !user) return;

        // Validate form based on reset type
        if (passwordResetForm.resetType === 'set_password') {
            if (!passwordResetForm.newPassword) {
                setError('New password is required');
                return;
            }

            if (passwordResetForm.newPassword.length < 8) {
                setError('New password must be at least 8 characters long');
                return;
            }

            if (passwordResetForm.newPassword !== passwordResetForm.confirmPassword) {
                setError('Passwords do not match');
                return;
            }
        }

        setProcessingPasswordReset(true);
        try {
            if (passwordResetForm.resetType === 'set_password') {
                // Admin sets new password directly
                await userAPI.adminResetPassword(id, {
                    newPassword: passwordResetForm.newPassword,
                    resetType: 'set_password'
                });
                setSuccess('Password has been reset successfully!');
            } else {
                // Generate temporary password and send via email
                await userAPI.adminResetPassword(id, {
                    newPassword: '', // Not needed for this type
                    resetType: 'send_reset_link'
                });
                setSuccess('Temporary password has been generated and sent to the user via email!');
            }

            setPasswordResetDialogOpen(false);
            
            // Clear success message after 5 seconds
            setTimeout(() => {
                setSuccess(null);
            }, 5000);
        } catch (err: any) {
            console.error('Error resetting password:', err);
            setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
        } finally {
            setProcessingPasswordReset(false);
        }
    };

    const handlePasswordResetFormChange = (field: string, value: string) => {
        setPasswordResetForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Get status color
    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'active': return 'success';
            case 'pending': return 'warning';
            case 'inactive': return 'error';
            case 'suspended': return 'error';
            case 'on_hold': return 'info';
            default: return 'default';
        }
    };

    // Get role color
    const getRoleColor = (role: string) => {
        switch (role?.toLowerCase()) {
            case 'admin': return 'primary';
            case 'employee': return 'secondary';
            case 'member': return 'default';
            case 'super_admin': return 'error';
            default: return 'default';
        }
    };

    // Safe date formatting helper
    const formatDate = (dateString: string | Date | null | undefined): string => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid Date';
            return date.toLocaleDateString();
        } catch {
            return 'Invalid Date';
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error && !user) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                >
                    Back to User Management
                </Button>
            </Container>
        );
    }

    if (!user) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    User not found
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                >
                    Back to User Management
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                
                <RouterLink
                    className='user-link'
                    style={{ display: 'flex', alignItems: 'center' }}
                    color="inherit"
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    className='user-link'
                    style={{ display: 'flex', alignItems: 'center' }}
                    color="inherit"
                    to="/user-management"
                >
                    User Management
                </RouterLink>
                <Typography color="text.primary">
                    {user.firstName} {user.lastName}
                </Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" component="h1">
                        User Details
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {/* Show password reset button for admin, super admin, and employee */}
                    {(currentUser?.role === 'admin' || currentUser?.role === 'super_admin' || currentUser?.role === 'employee') && (
                        <Tooltip title="Reset User Password">
                            <IconButton
                                color="warning"
                                onClick={handlePasswordResetOpen}
                            >
                                <LockResetIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton
                        color="error"
                        onClick={handleDeleteOpen}
                        title="Deactivate User"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Success/Error Messages */}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Card>
                        <CardHeader
                            title={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    Basic Information
                                    <IconButton
                                        color="primary"
                                        onClick={handleEditOpen}
                                        title="Edit Basic Information"
                                        sx={{ ml: 1 }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            }
                            avatar={<PersonIcon />}
                        />
                        <CardContent>
                            <List>
                                <ListItem divider>
                                    <ListItemText
                                        primary="Full Name"
                                        secondary={`${user.firstName} ${user.lastName}`}
                                    />
                                </ListItem>
                                <ListItem divider>
                                    <ListItemText
                                        primary="Email"
                                        secondary={user.email}
                                    />
                                </ListItem>
                                <ListItem divider>
                                    <ListItemText
                                        primary="Phone"
                                        secondary={user.phone || 'Not provided'}
                                    />
                                </ListItem>
                                <ListItem divider>
                                    <ListItemText
                                        primary="Role"
                                        secondary={
                                            <Box component="div">
                                                <Chip
                                                    label={user.role?.replace('_', ' ') || 'Unknown'}
                                                    size="small"
                                                    color={getRoleColor(user.role) as any}
                                                />
                                            </Box>
                                        }
                                        secondaryTypographyProps={{ component: 'div' }}
                                    />
                                </ListItem>
                                <ListItem divider>
                                    <ListItemText
                                        primary="Status"
                                        secondary={
                                            <Box component="div">
                                                <Chip
                                                    label={user.status?.replace('_', ' ') || 'Unknown'}
                                                    size="small"
                                                    color={getStatusColor(user.status) as any}
                                                />
                                            </Box>
                                        }
                                        secondaryTypographyProps={{ component: 'div' }}
                                    />
                                </ListItem>
                                <ListItem divider>
                                    <ListItemText
                                        primary="Created Date"
                                        secondary={formatDate(user.createdAt)}
                                    />
                                </ListItem>
                                {/* Additional Information fields moved here */}
                                <ListItem divider>
                                    <ListItemText
                                        primary="Address"
                                        secondary={user.address || 'Not provided'}
                                    />
                                </ListItem>
                                <ListItem divider>
                                    <ListItemText
                                        primary="City"
                                        secondary={user.city || 'Not provided'}
                                    />
                                </ListItem>
                                <ListItem divider>
                                    <ListItemText
                                        primary="State/Province"
                                        secondary={user.state || 'Not provided'}
                                    />
                                </ListItem>
                                <ListItem divider>
                                    <ListItemText
                                        primary="ZIP/Postal Code"
                                        secondary={user.zipCode || 'Not provided'}
                                    />
                                </ListItem>
                                <ListItem divider>
                                    <ListItemText
                                        primary="Country"
                                        secondary={user.country || 'Not provided'}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Date of Birth"
                                        secondary={formatDate(user.dateOfBirth)}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Member Profiles */}
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Card>
                        <CardHeader
                            title="Member Profiles"
                            avatar={<BadgeIcon />}
                        />
                        <CardContent>
                            {memberProfiles && memberProfiles.length > 0 ? (
                                <Stack spacing={3}>
                                    {memberProfiles.map((profile, index) => {
                                        // Defensive check for profile data
                                        if (!profile || !profile.id) {
                                            console.warn(`Invalid profile data at index ${index}:`, profile);
                                            return null;
                                        }
                                        
                                        return (
                                            <Box key={profile.id}>
                                                {index > 0 && <Divider sx={{ mb: 2 }} />}
                                                <Typography variant="h6" gutterBottom>
                                                    Profile #{index + 1}
                                                </Typography>
                                                <List>
                                                <ListItem divider>
                                                    <ListItemText
                                                        primary="Member Name"
                                                        secondary={`${profile.firstName} ${profile.lastName}`}
                                                    />
                                                </ListItem>
                                                <ListItem divider>
                                                    <ListItemText
                                                        primary="Member Email"
                                                        secondary={profile.email}
                                                    />
                                                </ListItem>
                                                {profile.phone && (
                                                    <ListItem divider>
                                                        <ListItemText
                                                            primary="Member Phone"
                                                            secondary={profile.phone}
                                                        />
                                                    </ListItem>
                                                )}
                                                <ListItem divider>
                                                    <ListItemText
                                                        primary="Membership Level"
                                                        secondary={profile.membershipTier?.name || 'Unknown Tier'}
                                                    />
                                                </ListItem>
                                                <ListItem divider>
                                                    <ListItemText
                                                        primary="Member Since"
                                                        secondary={formatDate(profile.startDate)}
                                                    />
                                                </ListItem>
                                                {profile.endDate && (
                                                    <ListItem divider>
                                                        <ListItemText
                                                            primary="Membership End Date"
                                                            secondary={formatDate(profile.endDate)}
                                                        />
                                                    </ListItem>
                                                )}
                                                <ListItem divider>
                                                    <ListItemText
                                                        primary="Status"
                                                        secondary={
                                                            <Box component="div">
                                                                <Chip
                                                                    label={profile.status}
                                                                    size="small"
                                                                    color={profile.status === 'active' ? 'success' : 'default'}
                                                                />
                                                            </Box>
                                                        }
                                                        secondaryTypographyProps={{ component: 'div' }}
                                                    />
                                                </ListItem>
                                                {profile.monthlyPrice && (
                                                    <ListItem divider>
                                                        <ListItemText
                                                            primary="Monthly Price"
                                                            secondary={`$${profile.monthlyPrice}`}
                                                        />
                                                    </ListItem>
                                                )}
                                                {profile.membershipNumber && (
                                                    <ListItem divider>
                                                        <ListItemText
                                                            primary="Membership Number"
                                                            secondary={profile.membershipNumber}
                                                        />
                                                    </ListItem>
                                                )}
                                                <ListItem divider>
                                                    <ListItemText
                                                        primary="Auto Renew"
                                                        secondary={profile.autoRenew ? 'Yes' : 'No'}
                                                    />
                                                </ListItem>
                                                {profile.groupRole && (
                                                    <ListItem divider>
                                                        <ListItemText
                                                            primary="Group Role"
                                                            secondary={
                                                                <Box component="div">
                                                                    <Chip
                                                                        label={profile.groupRole}
                                                                        size="small"
                                                                        color="primary"
                                                                    />
                                                                </Box>
                                                            }
                                                            secondaryTypographyProps={{ component: 'div' }}
                                                        />
                                                    </ListItem>
                                                )}
                                                {profile.address && (
                                                    <ListItem divider>
                                                        <ListItemText
                                                            primary="Member Address"
                                                            secondary={`${profile.address}${profile.city ? `, ${profile.city}` : ''}${profile.state ? `, ${profile.state}` : ''}${profile.zipCode ? ` ${profile.zipCode}` : ''}`}
                                                        />
                                                    </ListItem>
                                                )}
                                                {profile.dateOfBirth && (
                                                    <ListItem divider>
                                                        <ListItemText
                                                            primary="Date of Birth"
                                                            secondary={formatDate(profile.dateOfBirth)}
                                                        />
                                                    </ListItem>
                                                )}
                                                {profile.notes && (
                                                    <ListItem>
                                                        <ListItemText
                                                            primary="Notes"
                                                            secondary={profile.notes}
                                                        />
                                                    </ListItem>
                                                )}
                                            </List>
                                        </Box>
                                        );
                                    })}
                                </Stack>
                            ) : (
                                <Typography color="text.secondary">
                                    No member profiles found for this user.
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Edit User Dialog */}
            <Dialog
                open={editDialogOpen}
                disableEscapeKeyDown={true}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Edit User</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="First Name"
                                value={editForm.firstName}
                                onChange={(e) => handleEditChange('firstName', e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Last Name"
                                value={editForm.lastName}
                                onChange={(e) => handleEditChange('lastName', e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Email"
                                type="email"
                                value={editForm.email}
                                onChange={(e) => handleEditChange('email', e.target.value)}
                                fullWidth
                                required
                            />
                            {memberProfiles.length > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    Note: Changing the email will also update the email address in all {memberProfiles.length} associated member profile(s).
                                </Typography>
                            )}
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Phone"
                                value={editForm.phone}
                                onChange={(e) => handleEditChange('phone', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Role</InputLabel>
                                <Select
                                    value={editForm.role}
                                    onChange={(e) => handleEditChange('role', e.target.value)}
                                    label="Role"
                                >
                                    <MenuItem value="member">Member</MenuItem>
                                    <MenuItem value="employee">Employee</MenuItem>
                                    <MenuItem value="admin">Admin</MenuItem>
                                    <MenuItem value="coach">Coach</MenuItem>
                                    {currentUser?.role === 'super_admin' && (
                                        <MenuItem value="super_admin">Super Admin</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={editForm.status}
                                    onChange={(e) => handleEditChange('status', e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="suspended">Suspended</MenuItem>
                                    <MenuItem value="on_hold">On Hold</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditClose} disabled={processingEdit}>Cancel</Button>
                    <Button 
                        onClick={handleEditSubmit} 
                        variant="contained" 
                        color="primary"
                        disabled={processingEdit}
                    >
                        {processingEdit ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                {editForm.email !== user?.email && memberProfiles.length > 0 
                                    ? `Updating user and ${memberProfiles.length} profiles...`
                                    : 'Updating user...'
                                }
                            </Box>
                        ) : (
                            'Update User'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Password Reset Dialog */}
            <Dialog
                open={passwordResetDialogOpen}
                disableEscapeKeyDown={true}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reset User Password</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" gutterBottom>
                            <strong>User:</strong> {user?.firstName} {user?.lastName} ({user?.email})
                        </Typography>
                    </Box>

                    <FormControl component="fieldset" sx={{ mb: 3 }}>
                        <FormLabel component="legend">Reset Method</FormLabel>
                        <RadioGroup
                            value={passwordResetForm.resetType}
                            onChange={(e) => handlePasswordResetFormChange('resetType', e.target.value)}
                        >
                            <FormControlLabel
                                value="set_password"
                                control={<Radio />}
                                label="Set a specific password"
                            />
                            <FormControlLabel
                                value="send_reset_link"
                                control={<Radio />}
                                label="Generate temporary password and email to user"
                            />
                        </RadioGroup>
                    </FormControl>

                    {passwordResetForm.resetType === 'set_password' && (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    type={showPassword ? 'text' : 'password'}
                                    label="New Password"
                                    value={passwordResetForm.newPassword}
                                    onChange={(e) => handlePasswordResetFormChange('newPassword', e.target.value)}
                                    helperText="Password must be at least 8 characters long"
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                                aria-label="toggle password visibility"
                                            >
                                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    label="Confirm New Password"
                                    value={passwordResetForm.confirmPassword}
                                    onChange={(e) => handlePasswordResetFormChange('confirmPassword', e.target.value)}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                edge="end"
                                                aria-label="toggle confirm password visibility"
                                            >
                                                {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {passwordResetForm.resetType === 'send_reset_link' && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            A secure temporary password will be generated automatically and sent to the user via email. 
                            The user will be advised to change their password upon next login.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePasswordResetClose} disabled={processingPasswordReset}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handlePasswordResetSubmit}
                        variant="contained"
                        color="warning"
                        disabled={processingPasswordReset}
                        startIcon={processingPasswordReset ? <CircularProgress size={20} /> : <LockResetIcon />}
                    >
                        {processingPasswordReset ? 'Resetting...' : 'Reset Password'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                disableEscapeKeyDown={true}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ color: 'error.main' }}>
                    Deactivate User Account
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <strong>This operation is not recommended!</strong>
                    </Alert>
                    <DialogContentText>
                        Instead of permanently deleting this user account, we will set the account status to 'inactive'.
                        This preserves all user data and history while preventing login access.
                        <br /><br />
                        <strong>User:</strong> {user.firstName} {user.lastName} ({user.email})
                        <br /><br />
                        Are you sure you want to deactivate this account?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteClose}>Cancel</Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={processingDelete}
                    >
                        {processingDelete ? <CircularProgress size={20} /> : 'Deactivate Account'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

// Wrap the component with ErrorBoundary
const UserDetailsPageWithErrorBoundary: React.FC = () => {
    return (
        <ErrorBoundary
            fallbackTitle="User Details Error"
            fallbackMessage="An error occurred while loading the user details. This could be due to a network issue or invalid user data."
        >
            <UserDetailsPage />
        </ErrorBoundary>
    );
};

export default UserDetailsPageWithErrorBoundary;
